/**
 * The automations centre: **one modal** with two top-level tabs, Create (the recipe gallery)
 * and Manage (the installed automations) — `ui-reference/automations/INDEX.md`.
 *
 * Geometry from the captures at 1600 × 1000 and the 1440 × 900 parity shot: the modal is inset
 * **24 px** from the viewport with a 96 px header; the title reads `**Automations** <board
 * name>`; the segmented control is centred and the Manage side is badged with the **active**
 * count, exactly like the board header's `Automate / N`. Manage has **four** sub-tabs, as
 * monday does (parity card P-032): *Automations*, *Run history*, *My connections* and
 * *Account usage*. monday's last two are
 * dead ends that redirect to its autopilot hub; ours show the account's integration
 * connections and this board's run counters instead — see `ManagePanels.tsx`.
 */
import { Icon, icons, useConfirm, useToast } from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  recipeFromScratch,
  recipeFromTemplate,
  automationBlockRegistry,
  isTriggerBlock,
  type AutomationRecipe,
  type AutomationTemplate,
} from '@elizmonday/shared';
import { AutomationList, EMPTY_FILTERS, type AutomationFilters } from './AutomationList';
import { CreateGallery } from './CreateGallery';
import { RecipeEditor } from './RecipeEditor';
import { RunHistory } from './RunHistory';
import { ConnectionsPanel, UsagePanel } from './ManagePanels';
import { PopoverCaption, PopoverOption, SlotPopover } from './slots/SlotPopover';
import { sentenceChunks, sentenceText, type SentenceChunk } from './sentence';
import { noticeText } from './notices';
import type { AutomationSummaryView, AutomationsSource, PickerDataView, RunRowView, TemplateGalleryView } from './types';

export interface AutomationsCenterProps {
  source: AutomationsSource;
  boardId: string;
  boardName: string;
  accountName: string;
  onClose(): void;
  /** `?tab=` on open: the board header's **Integrate** and **Automate** share this modal (H5). */
  initialTab?: 'create' | 'manage';
  initialSub?: ManageTab;
}

/** The four sub-tabs monday's Manage side carries (P-032). */
export type ManageTab = 'automations' | 'runs' | 'connections' | 'usage';

const MANAGE_TABS: { id: ManageTab; label: string; testId: string }[] = [
  { id: 'automations', label: 'Automations', testId: 'automations-subtab-automations' },
  { id: 'runs', label: 'Run history', testId: 'automations-subtab-runs' },
  { id: 'connections', label: 'My connections', testId: 'automations-subtab-connections' },
  { id: 'usage', label: 'Account usage', testId: 'automations-subtab-usage' },
];

type EditorState =
  | null
  | { mode: 'create'; recipe: AutomationRecipe; fixedShape: boolean; template: AutomationTemplate | null }
  | { mode: 'edit'; recipe: AutomationRecipe; fixedShape: false; automationId: string };

export function AutomationsCenter({
  source,
  boardId,
  boardName,
  accountName,
  onClose,
  initialTab = 'create',
  initialSub = 'automations',
}: AutomationsCenterProps) {
  const toast = useToast();
  const confirm = useConfirm();
  const [tab, setTab] = useState<'create' | 'manage'>(initialTab);
  const [sub, setSub] = useState<ManageTab>(initialSub);
  const [rows, setRows] = useState<AutomationSummaryView[]>([]);
  /** The rendered sentence of the rows whose stored title is empty — chunked so values bold. */
  const [sentences, setSentences] = useState<ReadonlyMap<string, SentenceChunk[]>>(new Map());
  const [runs, setRuns] = useState<RunRowView[]>([]);
  const [pickers, setPickers] = useState<PickerDataView | null>(null);
  const [gallery, setGallery] = useState<TemplateGalleryView | null>(null);
  const [filters, setFilters] = useState<AutomationFilters>(EMPTY_FILTERS);
  const [compact, setCompact] = useState(false);
  const [includeSkipped, setIncludeSkipped] = useState(false);
  const [editor, setEditor] = useState<EditorState>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [scratchOpen, setScratchOpen] = useState(false);
  const scratchAnchor = useRef<HTMLButtonElement>(null);

  const reload = useCallback(async () => {
    const [list, picked] = await Promise.all([source.list({ boardId }), source.pickers(boardId)]);
    setPickers(picked);

    /*
     * The **sentence** each card shows (P-032, T-8d). monday prints the recipe with its
     * configured values in bold — "When **Status** changes to **Done**, notify **Roberto**"
     * (capture `15-after-create`) — and assembles it in the browser, because the only name
     * its API exposes is the generic one: all 20 imported automations on board 717364248
     * carry the stored title "When a status changes to something, notify someone", and 9 of
     * them carry no title at all. Ours is assembled the same way, from the same block
     * registry the editor uses, whenever the row brought its recipe along.
     *
     * The chunks keep the values apart from the fixed words so the card can bold them; the
     * flat text replaces `title`, so search, the trigger facets and the export all read the
     * sentence the card shows. A deactivated owner or a parked state never costs an
     * automation its sentence.
     */
    const chunked = new Map<string, SentenceChunk[]>();
    for (const row of list) {
      if (row.recipe === null || row.recipe === undefined) continue;
      chunked.set(row.id, sentenceChunks(row.recipe, picked));
    }

    /* A source whose list carries no recipe (an older server) still gets the rows whose
       stored title is empty, one detail read each — they would otherwise render blank. */
    const blank = list.filter((row) => row.title.trim().length === 0 && !chunked.has(row.id));
    if (blank.length > 0) {
      const fetched = await Promise.all(
        blank.map(async (row) => {
          try {
            const detail = await source.get(row.id);
            return { id: row.id, chunks: detail.recipe === null ? null : sentenceChunks(detail.recipe, picked) };
          } catch {
            return { id: row.id, chunks: null };
          }
        }),
      );
      for (const entry of fetched) if (entry.chunks !== null) chunked.set(entry.id, entry.chunks);
    }

    setSentences(chunked);
    setRows(
      list.map((row) => {
        const chunks = chunked.get(row.id);
        return chunks === undefined ? row : { ...row, title: sentenceText(chunks) };
      }),
    );
  }, [source, boardId]);

  useEffect(() => {
    void reload();
    void source.templates(boardId).then(setGallery);
  }, [reload, source, boardId]);

  useEffect(() => {
    if (tab !== 'manage' || sub !== 'runs') return;
    void source.runs({ boardId, includeSkipped }).then(setRuns);
  }, [tab, sub, source, boardId, includeSkipped]);

  const activeCount = rows.filter((row) => row.runnable).length;
  const owners = useMemo(() => {
    const map = new Map<string, NonNullable<AutomationSummaryView['owner']>>();
    for (const row of rows) if (row.owner !== null) map.set(row.owner.id, row.owner);
    return [...map.values()];
  }, [rows]);

  const openTemplate = (template: AutomationTemplate): void => {
    const recipe = recipeFromTemplate(template.recipeId);
    if (recipe === null) {
      toast('That template is not available on this board', { kind: 'negative' });
      return;
    }
    setError(null);
    setEditor({ mode: 'create', recipe, fixedShape: true, template });
  };

  const openScratch = (triggerBlock: string): void => {
    setScratchOpen(false);
    setError(null);
    setEditor({ mode: 'create', recipe: recipeFromScratch(triggerBlock), fixedShape: false, template: null });
  };

  const openEdit = async (row: AutomationSummaryView): Promise<void> => {
    const detail = await source.get(row.id);
    if (detail.recipe === null) {
      toast(noticeText(detail.noticeMessage) ?? 'This automation has no editable recipe', { kind: 'negative' });
      return;
    }
    setError(null);
    setEditor({ mode: 'edit', recipe: detail.recipe, fixedShape: false, automationId: row.id });
  };

  const submitEditor = async (): Promise<void> => {
    if (editor === null) return;
    setBusy(true);
    setError(null);
    try {
      if (editor.mode === 'create') await source.create({ boardId, recipe: editor.recipe });
      else await source.update({ id: editor.automationId, recipe: editor.recipe });
      await reload();
      setEditor(null);
      setTab('manage');
      setSub('automations');
      toast(editor.mode === 'create' ? 'Automation created' : 'Automation saved', { kind: 'positive' });
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (row: AutomationSummaryView, active: boolean): Promise<void> => {
    setError(null);
    // Optimistic, and rolled back by `reload()` when the api refuses (a parked automation).
    setRows((current) => current.map((entry) => (entry.id === row.id ? { ...entry, active, runnable: active } : entry)));
    try {
      await source.setActive({ id: row.id, active });
      await reload();
    } catch (cause) {
      setError(noticeText(row.noticeMessage) ?? messageOf(cause));
      await reload();
    }
  };

  /**
   * **P-200** — monday's column picker ends with **+ Add a new column**, so the centre creates
   * board columns. The slot says which types it will accept (`requiredColumnTypes`, narrowed by
   * the slot descriptor); the new column takes the first of them, and any type when the slot
   * accepts any, which is `text` — monday's own default for a column created with no type
   * chosen. `columns.create` names it after its type, exactly as the board's add-column row
   * does, and the pickers are reloaded so the sentence can read the new title back.
   *
   * Open question 303 stays open: no capture shows *which* panel monday opens here, so nothing
   * about a type gallery is invented — one row, one column, chosen in the same gesture.
   */
  const addColumn = async ({ columnTypes }: { columnTypes?: readonly string[] | undefined }): Promise<{ id: string; type: string } | null> => {
    setError(null);
    try {
      const created = await source.createColumn({ boardId, type: columnTypes?.[0] ?? 'text' });
      await reload();
      toast(`Column "${created.title}" added`, { kind: 'positive' });
      return { id: created.id, type: created.type };
    } catch (cause) {
      setError(messageOf(cause));
      return null;
    }
  };

  const duplicate = async (row: AutomationSummaryView): Promise<void> => {
    await source.duplicate({ id: row.id });
    await reload();
    toast('Automation duplicated. It arrives switched off.', { kind: 'positive' });
  };

  const remove = async (row: AutomationSummaryView): Promise<void> => {
    const ok = await confirm({
      title: 'Delete this automation?',
      body: 'It stops running immediately. Its run history stays.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await source.remove(row.id);
    await reload();
    toast('Automation deleted', { kind: 'positive' });
  };

  return (
    <div className="ez-automation-backdrop" data-testid="automations-center-backdrop">
      <div className="automations-center-component" role="dialog" aria-label="Automations" data-testid="automations-center">
        <header className="ez-automation-header">
          <h2 className="ez-automation-header__title">
            <strong>Automations</strong> <span>{boardName}</span>
          </h2>
          <div className="ez-automation-segmented" role="tablist" aria-label="Automations">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'create'}
              className="ez-automation-segmented__tab"
              data-testid="automations-tab-create"
              onClick={() => {
                setTab('create');
                setEditor(null);
              }}
            >
              Create
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'manage'}
              className="ez-automation-segmented__tab"
              data-testid="automations-tab-manage"
              onClick={() => {
                setTab('manage');
                setEditor(null);
              }}
            >
              {activeCount === 0 ? 'Manage' : `Manage / ${String(activeCount)}`}
            </button>
          </div>
          <div className="ez-automation-header__tools">
            {/* P-032: monday keeps a chat icon left of the ✕. Its own is the support chat,
                which this product does not run, so ours opens the board's run history — the
                one thing an admin standing at this header actually wants to read. */}
            <button
              type="button"
              className="ez-automation-close"
              aria-label="Open run history"
              title="Run history"
              data-testid="automations-header-runs"
              onClick={() => {
                setEditor(null);
                setTab('manage');
                setSub('runs');
              }}
            >
              <Icon icon={icons.comment} size={20} ignoreFocusStyle />
            </button>
            <button type="button" className="ez-automation-close" aria-label="Close" data-testid="automations-close" onClick={onClose}>
              <Icon icon={icons.close} size={20} ignoreFocusStyle />
            </button>
          </div>
        </header>

        {editor !== null && pickers !== null ? (
          <RecipeEditor
            recipe={editor.recipe}
            pickers={pickers}
            fixedShape={editor.fixedShape}
            busy={busy}
            error={error}
            submitLabel={editor.mode === 'edit' ? 'Save automation' : 'Create automation'}
            onChange={(recipe) => setEditor((current) => (current === null ? current : { ...current, recipe }))}
            onAddColumn={addColumn}
            onBack={() => setEditor(null)}
            onSubmit={() => void submitEditor()}
          />
        ) : tab === 'create' ? (
          gallery === null ? (
            <div className="ez-automation-loading" data-testid="automations-loading">
              Loading templates…
            </div>
          ) : (
            <CreateGallery
              gallery={gallery}
              accountName={accountName}
              installedCount={rows.length}
              onUseTemplate={openTemplate}
              scratchRef={scratchAnchor}
              onCreateFromScratch={() => setScratchOpen(true)}
              onShowInstalled={() => {
                setTab('manage');
                setSub('automations');
              }}
            />
          )
        ) : (
          <div className="ez-automation-manage" data-testid="automations-manage">
            <div className="ez-automation-manage__head">
              <h3 className="ez-automation-manage__title">Manage your board automations</h3>
              {/*
                * P-032: monday puts `⊕ Autopilot hub` left of `Create automation`. The hub is
                * monday's own account-wide surface; everything it holds lives in the two tabs
                * below on this product, and the control says so rather than going nowhere.
                */}
              <button
                type="button"
                className="ez-automation-hub"
                data-testid="automations-autopilot-hub"
                disabled
                title="monday's account-wide hub. Connections and usage are the two tabs below."
              >
                <Icon icon={icons.automation} size={16} ignoreFocusStyle />
                Autopilot hub
              </button>
              <button
                ref={scratchAnchor}
                type="button"
                className="ez-automation-primary ez-automation-primary--split"
                data-testid="automations-create-button"
                onClick={() => setScratchOpen(true)}
              >
                Create automation
                <Icon icon={icons.chevronDown} size={16} ignoreFocusStyle />
              </button>
            </div>

            <div className="ez-automation-subtabs" role="tablist" aria-label="Manage">
              {MANAGE_TABS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  role="tab"
                  aria-selected={sub === entry.id}
                  className="ez-automation-subtabs__tab"
                  data-testid={entry.testId}
                  onClick={() => setSub(entry.id)}
                >
                  {entry.label}
                </button>
              ))}
            </div>

            {sub === 'automations' ? (
              <AutomationList
                rows={rows}
                sentences={sentences}
                filters={filters}
                onFiltersChange={setFilters}
                owners={owners}
                compact={compact}
                onCompactChange={setCompact}
                error={error}
                onToggle={(row, active) => void toggle(row, active)}
                onEdit={(row) => void openEdit(row)}
                onDuplicate={(row) => void duplicate(row)}
                onDelete={(row) => void remove(row)}
                onExploreTemplates={() => setTab('create')}
              />
            ) : sub === 'connections' ? (
              <ConnectionsPanel pickers={pickers} />
            ) : sub === 'usage' ? (
              <UsagePanel rows={rows} />
            ) : (
              <RunHistory
                runs={runs}
                includeSkipped={includeSkipped}
                onIncludeSkippedChange={setIncludeSkipped}
                loadSteps={(runId) => source.runSteps(runId)}
                onViewAutomation={(automationId) => {
                  const row = rows.find((entry) => entry.id === automationId);
                  setSub('automations');
                  if (row !== undefined) void openEdit(row);
                }}
              />
            )}
          </div>
        )}

        {scratchOpen ? (
          <SlotPopover
            anchorRef={scratchAnchor}
            label="Choose a trigger"
            width={340}
            onDismiss={() => setScratchOpen(false)}
            testId="automations-scratch-popover"
          >
            <PopoverCaption>Start with a trigger</PopoverCaption>
            {automationBlockRegistry
              .list()
              .filter(isTriggerBlock)
              .map((block) => (
                <PopoverOption key={block.id} testId={`automations-scratch-${block.id}`} onClick={() => openScratch(block.id)}>
                  {block.sentencePartial.replace(/\{([^{},]*),\s*[A-Za-z0-9_]+\}/g, '$1')}
                </PopoverOption>
              ))}
          </SlotPopover>
        ) : null}
      </div>
    </div>
  );
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}
